/**
 * challengeAutoSubmit — Batch-query active challenges and auto-submit
 * qualifying strength events. Fixes the N+1 query pattern from the
 * original inline implementation.
 */
import { Platform } from 'react-native';
import Constants from 'expo-constants';
import { supabase } from '@/lib/supabase';
import { CHALLENGE_SCORE_COLUMN_V1 } from '@/lib/challenges/score';
import { createLogger } from '@/lib/logger';

const log = createLogger('ChallengeAutoSubmit');

const V1_LIFT_KEYS = ['bench', 'squat', 'deadlift'] as const;

export interface AutoSubmitProfile {
  id: string;
  gym_id?: string | null;
}

/**
 * Auto-submit qualifying sets to active e1rm challenges.
 * Fetches all active challenges, participants, recent events, and existing
 * submissions in batch queries — then processes locally.
 */
export async function autoSubmitChallenges(
  profile: AutoSubmitProfile,
  authUserId: string,
): Promise<{ inserted: number; skipped: number }> {
  const scopeId = profile.gym_id || null;
  if (!scopeId) {
    if (__DEV__) log.info('no scope/gym id available, skipping');
    return { inserted: 0, skipped: 0 };
  }

  const nowIso = new Date().toISOString();

  // 1. Batch: all active V1 challenges for this gym
  const { data: activeChallenges, error: acErr } = await supabase
    .from('challenges')
    .select('*')
    .eq('scope_type', 'gym')
    .eq('scope_id', scopeId)
    .eq('metric_type', CHALLENGE_SCORE_COLUMN_V1)
    .lte('starts_at', nowIso)
    .gte('ends_at', nowIso)
    .in('lift_key', [...V1_LIFT_KEYS]);

  if (acErr) {
    log.warn('failed to load active challenges', acErr.message || acErr);
    return { inserted: 0, skipped: 0 };
  }

  const active = Array.isArray(activeChallenges) ? activeChallenges : [];
  if (__DEV__) log.info('activeChallengesCount', active.length);
  if (active.length === 0) return { inserted: 0, skipped: 0 };

  const challengeIds = active.map((c: any) => c.id).filter(Boolean);

  // 2. Batch: which of these the user is opted into
  const { data: participants, error: partErr } = await supabase
    .from('challenge_participants')
    .select('challenge_id')
    .in('challenge_id', challengeIds)
    .eq('profile_id', profile.id)
    .eq('status', 'opted_in');

  if (partErr) {
    log.warn('failed to load participants', partErr.message || partErr);
    return { inserted: 0, skipped: 0 };
  }

  const optedInSet = new Set(
    (Array.isArray(participants) ? participants : []).map((p: any) => p.challenge_id),
  );
  if (__DEV__) log.info('optedInCount', optedInSet.size);
  if (optedInSet.size === 0) return { inserted: 0, skipped: 0 };

  // Filter to only opted-in challenges
  const relevantChallenges = active.filter((c: any) => optedInSet.has(c.id));

  // 3. Batch: recent strength events (last 2 min)
  const cutoff = new Date(Date.now() - 2 * 60 * 1000).toISOString();
  const { data: recentEvents, error: evErr } = await supabase
    .from('strength_events')
    .select('*')
    .eq('profile_id', profile.id)
    .gte('performed_at', cutoff)
    .in('lift_key', [...V1_LIFT_KEYS])
    .order('performed_at', { ascending: false })
    .limit(10);

  if (evErr) {
    log.warn('failed to load recent strength_events', evErr.message || evErr);
    return { inserted: 0, skipped: 0 };
  }

  const events = Array.isArray(recentEvents) ? recentEvents : [];
  if (__DEV__) log.info('recentEventsCount', events.length);
  if (events.length === 0) return { inserted: 0, skipped: 0 };

  // 4. Batch: existing submissions for dedup (all relevant challenge+profile combos)
  const relevantChallengeIds = relevantChallenges.map((c: any) => c.id);
  const { data: existingSubs } = await supabase
    .from('challenge_lift_submissions')
    .select('challenge_id, lift_key, performed_at')
    .in('challenge_id', relevantChallengeIds)
    .eq('profile_id', profile.id);

  const existingKeys = new Set(
    (existingSubs || []).map(
      (s: any) => `${s.challenge_id}:${s.lift_key}:${s.performed_at}`,
    ),
  );

  // Build index: lift_key -> challenges
  const challengesByLift = new Map<string, any[]>();
  for (const c of relevantChallenges) {
    const lk = (c as any).lift_key;
    if (!lk) continue;
    if (!challengesByLift.has(lk)) challengesByLift.set(lk, []);
    challengesByLift.get(lk)!.push(c);
  }

  // 5. Process locally — no more per-event queries
  const submissions: any[] = [];
  let skipped = 0;

  const clientPlatformDefault = Platform.OS === 'web' ? 'web' : Platform.OS;
  const clientAppVersionDefault =
    (Constants.expoConfig as any)?.version ||
    (Constants as any)?.manifest?.version ||
    'dev';

  for (const ev of events) {
    const liftKey = ev.lift_key;
    if (!liftKey) continue;
    const matchingChallenges = challengesByLift.get(liftKey);
    if (!matchingChallenges || matchingChallenges.length === 0) continue;

    for (const challenge of matchingChallenges) {
      const dedupKey = `${challenge.id}:${liftKey}:${ev.performed_at}`;
      if (existingKeys.has(dedupKey)) {
        skipped++;
        if (__DEV__) log.info('dedupe skip', { challengeId: challenge.id, performed_at: ev.performed_at });
        continue;
      }

      const wUnit = ev.weight_unit || 'lb';
      const w = Number(ev.weight || 0);
      const r = Number(ev.reps || 0);
      const weightKg = wUnit === 'lb' ? w * 0.45359237 : w;

      submissions.push({
        challenge_id: challenge.id,
        profile_id: profile.id,
        auth_user_id: authUserId,
        lift_key: liftKey,
        weight: w,
        weight_unit: wUnit,
        weight_kg: weightKg,
        reps: r,
        metric_key: 'duel_standard_strength',
        formula_version: 1,
        computed_at: new Date().toISOString(),
        performed_at: ev.performed_at,
        timezone_offset_minutes:
          typeof ev.timezone_offset_minutes === 'number'
            ? ev.timezone_offset_minutes
            : -new Date().getTimezoneOffset(),
        client_platform: ev.client_platform || clientPlatformDefault,
        client_app_version: ev.client_app_version || clientAppVersionDefault,
        quality_tier: 0,
        range_of_motion: ev.range_of_motion || 'unknown',
        source: 'auto',
      });

      // Mark as seen so we don't double-insert within this batch
      existingKeys.add(dedupKey);
    }
  }

  if (submissions.length === 0) {
    if (__DEV__) log.info('summary', { recentEvents: events.length, inserted: 0, skipped });
    return { inserted: 0, skipped };
  }

  // 6. Batch insert all submissions at once
  const { error: insertErr } = await supabase
    .from('challenge_lift_submissions')
    .insert(submissions);

  if (insertErr) {
    log.warn('batch insert error', insertErr.message || insertErr);
    return { inserted: 0, skipped };
  }

  if (__DEV__) log.info('summary', { recentEvents: events.length, inserted: submissions.length, skipped });
  return { inserted: submissions.length, skipped };
}
